import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Users, Plus, Edit, Trash2, Eye, Filter, UserCheck, UserX, Clock, Upload, FileSpreadsheet, Download, Loader2, RefreshCcw, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { supabase } from "@/lib/supabase";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { format, formatDistanceToNow } from "date-fns";
import { UserCriteriaViewDialog } from "./UserCriteriaViewDialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const criteriaFormSchema = z.object({
  name: z.string().min(1, "Criteria name is required"),
  description: z.string().optional(),
  criteriaType: z.string().min(1, "Criteria type is required"),
  criteriaParams: z.record(z.any()),
  updateFrequency: z.string().default("manual")
});

type CriteriaForm = z.infer<typeof criteriaFormSchema>;

interface UserCriteria {
  id: number;
  name: string;
  description?: string;
  criteriaType: string;
  criteriaParams: Record<string, any>;
  isActive: boolean;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  creatorName?: string;
  matchingUserCount?: number;
  lastUpdatedAt?: string | null;
}

export default function CRMCriteriaTab() {
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedCriteria, setSelectedCriteria] = useState<UserCriteria | null>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedType, setSelectedType] = useState<string>("all");
  const [isBulkImportDialogOpen, setIsBulkImportDialogOpen] = useState(false);
  const [bulkImportCriteria, setBulkImportCriteria] = useState<UserCriteria | null>(null);
  const [selectedImportId, setSelectedImportId] = useState<number | null>(null);
  const [selectedImportDetails, setSelectedImportDetails] = useState<any | null>(null);
  const [isImportDetailsLoading, setIsImportDetailsLoading] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<Array<{ row: number; issues: string[] }>>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { data: criteriaData, isLoading, refetch: refetchCriteria } = useQuery({
    queryKey: ['/api/admin/crm/criteria', currentPage, selectedType],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20'
      });
      if (selectedType !== 'all') params.append('type', selectedType);

      const response = await apiRequest('GET', `/api/admin/crm/criteria?${params}`);
      return response.json();
    }
  });

  const criteria = criteriaData?.criteria || [];

  const {
    data: bulkImportsData,
    isFetching: isBulkImportsFetching,
    refetch: refetchBulkImports,
  } = useQuery({
    queryKey: ['bulk-imports', bulkImportCriteria?.id],
    enabled: isBulkImportDialogOpen && !!bulkImportCriteria,
    queryFn: async () => {
      if (!bulkImportCriteria) {
        return { imports: [] };
      }
      const response = await apiRequest(
        'GET',
        `/api/admin/crm/criteria/${bulkImportCriteria.id}/bulk-imports`
      );
      return response.json();
    },
  });

  const bulkImportHistory = bulkImportsData?.imports ?? [];
  const currentImportInfo = selectedImportDetails?.import ?? null;
  const currentImportContacts = (selectedImportDetails?.contacts as any[]) ?? [];
  const currentImportValidContacts = currentImportContacts.filter(
    (contact) => !Array.isArray(contact.validationErrors) || contact.validationErrors.length === 0
  ).length;
  const currentImportInvalidContacts = currentImportContacts.filter(
    (contact) => Array.isArray(contact.validationErrors) && contact.validationErrors.length > 0
  ).length;
  const currentImportMatchedContacts = currentImportContacts.filter(
    (contact) => Boolean(contact.matchedUserId)
  ).length;

  const createForm = useForm<CriteriaForm>({
    resolver: zodResolver(criteriaFormSchema),
    defaultValues: {
      name: "",
      description: "",
      criteriaType: "new_users",
      criteriaParams: { days: 7 },
      updateFrequency: "manual"
    }
  });

  const editForm = useForm<CriteriaForm>({
    resolver: zodResolver(criteriaFormSchema)
  });

  const createMutation = useMutation({
    mutationFn: async (data: CriteriaForm) => {
      const response = await apiRequest('POST', '/api/admin/crm/criteria', {
        body: data
      });
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Criteria created successfully!" });
      setIsCreateDialogOpen(false);
      createForm.reset();
      refetchCriteria();
      queryClient.invalidateQueries({ queryKey: ['/api/admin/crm/criteria'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error creating criteria",
        description: error.message || "Failed to create criteria",
        variant: "destructive"
      });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<CriteriaForm> }) => {
      const response = await apiRequest('PATCH', `/api/admin/crm/criteria/${id}`, {
        body: data
      });
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Criteria updated successfully!" });
      setIsEditDialogOpen(false);
      setSelectedCriteria(null);
      refetchCriteria();
      queryClient.invalidateQueries({ queryKey: ['/api/admin/crm/criteria'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error updating criteria",
        description: error.message || "Failed to update criteria",
        variant: "destructive"
      });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest('DELETE', `/api/admin/crm/criteria/${id}`);
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Criteria deleted successfully!" });
      refetchCriteria();
      queryClient.invalidateQueries({ queryKey: ['/api/admin/crm/criteria'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting criteria",
        description: error.message || "Failed to delete criteria",
        variant: "destructive"
      });
    }
  });

  const previewMutation = useMutation({
    mutationFn: async (criteria: { criteriaType: string; criteriaParams: any; criteriaId?: number }) => {
      const response = await apiRequest('POST', '/api/admin/crm/criteria/preview', {
        body: criteria
      });
      return response.json();
    },
    onSuccess: (data) => {
      setPreviewData(data);
      setIsPreviewDialogOpen(true);
    },
    onError: (error: any) => {
      toast({
        title: "Error previewing criteria",
        description: error.message || "Failed to preview criteria",
        variant: "destructive"
      });
    }
  });

  const handleEdit = (criteria: UserCriteria) => {
    setSelectedCriteria(criteria);
    editForm.reset({
      name: criteria.name,
      description: criteria.description || "",
      criteriaType: criteria.criteriaType,
      criteriaParams: criteria.criteriaParams,
      updateFrequency: (criteria as any).updateFrequency || "manual"
    });
    setIsEditDialogOpen(true);
  };

  const handlePreview = (criteria: UserCriteria) => {
    previewMutation.mutate({
      criteriaType: criteria.criteriaType,
      criteriaParams: {
        ...criteria.criteriaParams,
        criteriaId: criteria.id,
      },
      criteriaId: criteria.id,
    });
  };

  const handleViewUsers = (criteria: UserCriteria) => {
    setSelectedCriteria(criteria);
    setIsViewDialogOpen(true);
  };

  const handleBulkImportManage = (criteria: UserCriteria) => {
    setBulkImportCriteria(criteria);
    setSelectedImportId(null);
    setSelectedImportDetails(null);
    setUploadErrors([]);
    setUploadFile(null);
    setIsBulkImportDialogOpen(true);
  };

  const loadImportDetails = useCallback(
    async (importId: number) => {
      if (!bulkImportCriteria) return;
      setSelectedImportId(importId);
      setIsImportDetailsLoading(true);
      try {
        const response = await apiRequest(
          'GET',
          `/api/admin/crm/criteria/${bulkImportCriteria.id}/bulk-imports/${importId}`
        );
        const data = await response.json();
        setSelectedImportDetails(data);
      } catch (error: any) {
        console.error('Error loading bulk import details:', error);
        toast({
          title: "Failed to load contacts",
          description: error?.message || "Could not load import details",
          variant: "destructive",
        });
      } finally {
        setIsImportDetailsLoading(false);
      }
    },
    [bulkImportCriteria, toast]
  );

  const handleBulkUpload = async () => {
    if (!bulkImportCriteria) {
      toast({
        title: "No criteria selected",
        description: "Choose a criteria to upload contacts.",
        variant: "destructive",
      });
      return;
    }

    if (!uploadFile) {
      toast({
        title: "No file selected",
        description: "Choose a CSV or Excel file to import.",
        variant: "destructive",
      });
      return;
    }

    const allowedExtensions = ['csv', 'xlsx', 'xls'];
    const extension = uploadFile.name.split('.').pop()?.toLowerCase();
    if (!extension || !allowedExtensions.includes(extension)) {
      toast({
        title: "Unsupported file type",
        description: "Upload a CSV, XLSX, or XLS file.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setUploadErrors([]);

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const response = await apiRequest(
        'POST',
        `/api/admin/crm/criteria/${bulkImportCriteria.id}/bulk-import`,
        { body: formData }
      );
      const data = await response.json();

      toast({
        title: "Bulk import complete",
        description: `Processed ${data.totalRows} rows (${data.validRows} valid, ${data.invalidRows} invalid).`,
      });

      setUploadErrors(data.errors || []);
      setUploadFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      await refetchBulkImports();
      await refetchCriteria();
      queryClient.invalidateQueries({ queryKey: ['/api/admin/crm/criteria'] });

      if (data.import?.id) {
        await loadImportDetails(data.import.id);
      }
    } catch (error: any) {
      console.error('Bulk import upload failed:', error);
      toast({
        title: "Bulk import failed",
        description: error?.message || "Unable to process the uploaded file.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadImport = async (importId: number) => {
    if (!bulkImportCriteria) {
      return;
    }

    try {
      const response = await apiRequest(
        'GET',
        `/api/admin/crm/criteria/${bulkImportCriteria.id}/bulk-imports/${importId}/download`,
        {
          headers: { Accept: 'text/csv' },
        }
      );

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bulk-import-${bulkImportCriteria.id}-${importId}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to download bulk import:', error);
      toast({
        title: "Download failed",
        description: error?.message || "Could not download the import file.",
        variant: "destructive",
      });
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiRequest(
        'GET',
        '/api/admin/crm/criteria/bulk-import/template',
        { headers: { Accept: 'text/csv' } }
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'user-criteria-bulk-import-template.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to download template:', error);
      toast({
        title: 'Template download failed',
        description: error?.message || 'Could not download the template file.',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    if (!isBulkImportDialogOpen) {
      setBulkImportCriteria(null);
      setSelectedImportId(null);
      setSelectedImportDetails(null);
      setUploadFile(null);
      setUploadErrors([]);
      return;
    }

    if (bulkImportCriteria) {
      refetchBulkImports();
    }
  }, [isBulkImportDialogOpen, bulkImportCriteria, refetchBulkImports]);

  useEffect(() => {
    if (!isBulkImportDialogOpen || !bulkImportCriteria) {
      return;
    }

    if (bulkImportsData?.imports?.length) {
      const latestImport = bulkImportsData.imports[0];
      if (selectedImportId === null) {
        loadImportDetails(latestImport.id);
      }
    } else {
      setSelectedImportId(null);
      setSelectedImportDetails(null);
    }
  }, [
    isBulkImportDialogOpen,
    bulkImportCriteria,
    bulkImportsData,
    selectedImportId,
    loadImportDetails,
  ]);

  const getCriteriaTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'new_users': 'New Users',
      'inactive_users': 'Inactive Users',
      'birthday_users': 'Birthday Users',
      'specific_users': 'Specific Users',
      'custom': 'Custom Query',
      'all_users': 'All Users',
      'tier_users': 'Tier-Based Users',
      'marketing_consent': 'Marketing Consent',
      'internal_testing': '🧪 Internal Testing',
      'bulk_import': 'Bulk Import',
      'weekly_competition_engagement': 'Weekly Competition Engagement',
    };
    return labels[type] || type;
  };

  const getCriteriaDescription = (criteria: UserCriteria) => {
    const { criteriaType, criteriaParams } = criteria;
    
    switch (criteriaType) {
      case 'new_users':
        return `Users registered in the last ${criteriaParams.days} days`;
      case 'inactive_users':
        return `Users inactive for ${criteriaParams.days} days`;
      case 'birthday_users':
        return `Users whose birthday is today (UTC+2)`;
      case 'specific_users':
        return `${criteriaParams.userIds?.length || 0} specific users`;
      case 'bulk_import': {
        const count = criteria.matchingUserCount || 0;
        const updated = criteria.lastUpdatedAt
          ? `Last import ${format(new Date(criteria.lastUpdatedAt), 'PPp')}`
          : 'No imports yet';
        return `${count} imported contacts. ${updated}.`;
      }
      case 'internal_testing':
        return `🧪 Testing with ${criteriaParams.userIds?.length || 0} selected users`;
      case 'tier_users':
        return `Users in tier: ${criteriaParams.tier}`;
      case 'marketing_consent':
        return `Users with marketing consent: ${criteriaParams.consent ? 'Yes' : 'No'}`;
      default:
        return criteria.description || 'Custom criteria';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" />
            User Criteria
          </h2>
          <p className="text-muted-foreground mt-1">
            Create and manage reusable user targeting criteria for campaigns
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-create-criteria">
              <Plus className="h-4 w-4 mr-2" />
              Create Criteria
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create User Criteria</DialogTitle>
              <DialogDescription>
                Define criteria to target specific user segments
              </DialogDescription>
            </DialogHeader>
            <Form {...createForm}>
              <form onSubmit={createForm.handleSubmit((data) => createMutation.mutate(data))} className="space-y-4">
                <FormField
                  control={createForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Criteria Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., New Users Last 7 Days" data-testid="input-criteria-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={createForm.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description (Optional)</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Describe this criteria..." data-testid="textarea-criteria-description" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={createForm.control}
                  name="criteriaType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Criteria Type</FormLabel>
                      <Select onValueChange={(value) => {
                        field.onChange(value);
                        switch (value) {
                          case 'new_users':
                            createForm.setValue('criteriaParams', { days: 7 });
                            break;
                          case 'inactive_users':
                            createForm.setValue('criteriaParams', { days: 30 });
                            break;
                          case 'birthday_users':
                            createForm.setValue('criteriaParams', {});
                            break;
                          case 'tier_users':
                            createForm.setValue('criteriaParams', { tier: 'bronze' });
                            break;
                          case 'marketing_consent':
                            createForm.setValue('criteriaParams', { consent: true });
                            break;
                          case 'all_users':
                            createForm.setValue('criteriaParams', {});
                            break;
                          case 'weekly_competition_engagement':
                            createForm.setValue('criteriaParams', { enteredAll: false });
                            break;
                          case 'internal_testing':
                            createForm.setValue('criteriaParams', { userIds: [] });
                            break;
                          default:
                            createForm.setValue('criteriaParams', {});
                        }
                      }} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-criteria-type">
                            <SelectValue placeholder="Select criteria type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="new_users">New Users</SelectItem>
                          <SelectItem value="inactive_users">Inactive Users</SelectItem>
                          <SelectItem value="birthday_users">Birthday Users (UTC+2)</SelectItem>
                          <SelectItem value="tier_users">Tier-Based Users</SelectItem>
                          <SelectItem value="marketing_consent">Marketing Consent</SelectItem>
                          <SelectItem value="all_users">All Users</SelectItem>
                          <SelectItem value="bulk_import">Bulk Import</SelectItem>
                          <SelectItem value="weekly_competition_engagement">Weekly Competition Engagement</SelectItem>
                          <SelectItem value="internal_testing">🧪 Internal Testing</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <CriteriaParamsForm form={createForm} />
                <FormField
                  control={createForm.control}
                  name="updateFrequency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Auto-Update Frequency</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-update-frequency">
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="manual">Manual Only</SelectItem>
                          <SelectItem value="hourly">Every Hour</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        How often should this criteria automatically refresh its matching users?
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)} data-testid="button-cancel-create">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createMutation.isPending} data-testid="button-submit-create">
                    {createMutation.isPending ? "Creating..." : "Create Criteria"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Saved Criteria</CardTitle>
              <CardDescription>Manage your user targeting criteria</CardDescription>
            </div>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-[200px]" data-testid="filter-criteria-type">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="new_users">New Users</SelectItem>
                <SelectItem value="inactive_users">Inactive Users</SelectItem>
                <SelectItem value="birthday_users">Birthday Users</SelectItem>
                <SelectItem value="tier_users">Tier-Based</SelectItem>
                <SelectItem value="marketing_consent">Marketing Consent</SelectItem>
                <SelectItem value="bulk_import">Bulk Import</SelectItem>
                <SelectItem value="internal_testing">🧪 Internal Testing</SelectItem>
                <SelectItem value="weekly_competition_engagement">Weekly Competition Engagement</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading criteria...</div>
          ) : criteria.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No criteria found. Create your first criteria to get started.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {criteria.map((item: UserCriteria) => (
                <Card key={item.id} className="border-2" data-testid={`criteria-card-${item.id}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold" data-testid={`text-criteria-name-${item.id}`}>{item.name}</h3>
                          <Badge variant={item.isActive ? "default" : "secondary"} data-testid={`badge-status-${item.id}`}>
                            {item.isActive ? "Active" : "Inactive"}
                          </Badge>
                          <Badge variant="outline" data-testid={`badge-type-${item.id}`}>
                            {getCriteriaTypeLabel(item.criteriaType)}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3" data-testid={`text-criteria-desc-${item.id}`}>
                          {getCriteriaDescription(item)}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {format(new Date(item.createdAt), "MMM d, yyyy")}
                          </span>
                          {item.creatorName && (
                            <span>Created by {item.creatorName}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {item.criteriaType === 'bulk_import' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleBulkImportManage(item)}
                            data-testid={`button-bulk-import-${item.id}`}
                          >
                            <Upload className="h-4 w-4 mr-1" />
                            Bulk Import
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewUsers(item)}
                          data-testid={`button-view-users-${item.id}`}
                        >
                          <Users className="h-4 w-4 mr-1" />
                          View Users
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePreview(item)}
                          disabled={previewMutation.isPending}
                          data-testid={`button-preview-${item.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(item)}
                          data-testid={`button-edit-${item.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" data-testid={`button-delete-${item.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Criteria</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{item.name}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid={`button-cancel-delete-${item.id}`}>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteMutation.mutate(item.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                data-testid={`button-confirm-delete-${item.id}`}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit User Criteria</DialogTitle>
            <DialogDescription>
              Update the criteria definition
            </DialogDescription>
          </DialogHeader>
          {selectedCriteria && (
            <Form {...editForm}>
              <form onSubmit={editForm.handleSubmit((data) => updateMutation.mutate({ id: selectedCriteria.id, data }))} className="space-y-4">
                <FormField
                  control={editForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Criteria Name</FormLabel>
                      <FormControl>
                        <Input {...field} data-testid="input-edit-criteria-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description (Optional)</FormLabel>
                      <FormControl>
                        <Textarea {...field} data-testid="textarea-edit-criteria-description" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="criteriaType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Criteria Type</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-edit-criteria-type">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="new_users">New Users</SelectItem>
                          <SelectItem value="inactive_users">Inactive Users</SelectItem>
                          <SelectItem value="birthday_users">Birthday Users</SelectItem>
                          <SelectItem value="tier_users">Tier-Based Users</SelectItem>
                          <SelectItem value="marketing_consent">Marketing Consent</SelectItem>
                          <SelectItem value="all_users">All Users</SelectItem>
                          <SelectItem value="bulk_import">Bulk Import</SelectItem>
                          <SelectItem value="weekly_competition_engagement">Weekly Competition Engagement</SelectItem>
                          <SelectItem value="internal_testing">🧪 Internal Testing</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <CriteriaParamsForm form={editForm} />
                <FormField
                  control={editForm.control}
                  name="updateFrequency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Auto-Update Frequency</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-edit-update-frequency">
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="manual">Manual Only</SelectItem>
                          <SelectItem value="hourly">Every Hour</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        How often should this criteria automatically refresh its matching users?
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)} data-testid="button-cancel-edit">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={updateMutation.isPending} data-testid="button-submit-edit">
                    {updateMutation.isPending ? "Updating..." : "Update Criteria"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Criteria Preview</DialogTitle>
            <DialogDescription>
              Users matching this criteria
            </DialogDescription>
          </DialogHeader>
      {previewData && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="text-lg px-4 py-2">
              <UserCheck className="h-5 w-5 mr-2" />
                  Total recipients: {previewData.totalCount}
                </Badge>
                {previewData.users && previewData.users.length > 0 && (
                  <Badge variant="outline" className="text-sm">
                    <Users className="h-4 w-4 mr-1" />
                    {previewData.users.length} matched users
                  </Badge>
                )}
                {previewData.contacts && previewData.contacts.length > 0 && (
                  <Badge variant="outline" className="text-sm">
                    <FileSpreadsheet className="h-4 w-4 mr-1" />
                    {previewData.contacts.length} imported contacts
                  </Badge>
                )}
              </div>
              {previewData.users && previewData.users.length > 0 ? (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Tier</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData.users.slice(0, 100).map((user: any) => (
                        <TableRow key={user.id}>
                          <TableCell>{user.id}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.firstName} {user.lastName}</TableCell>
                          <TableCell><Badge variant="outline">{user.currentTier || 'N/A'}</Badge></TableCell>
                          <TableCell>
                            {user.isActive ? (
                              <Badge variant="default">Active</Badge>
                            ) : (
                              <Badge variant="secondary">Inactive</Badge>
                            )}
                          </TableCell>
                          <TableCell>{format(new Date(user.createdAt), "MMM d, yyyy")}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground border rounded-md">
                  <UserX className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No existing users match this criteria.</p>
                </div>
              )}

              {previewData.contacts && previewData.contacts.length > 0 && (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Matched User</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData.contacts.slice(0, 200).map((contact: any, index: number) => (
                        <TableRow key={`${contact.email}-${index}`}>
                          <TableCell>{contact.email}</TableCell>
                          <TableCell>{contact.firstName} {contact.lastName}</TableCell>
                          <TableCell>{contact.phone || '—'}</TableCell>
                          <TableCell>{contact.matchedUserId ? `User #${contact.matchedUserId}` : 'Unmatched'}</TableCell>
                          <TableCell>
                            {contact.validationErrors && contact.validationErrors.length > 0 ? (
                              <Badge variant="destructive" className="text-xs">
                                {contact.validationErrors[0]}
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">Valid</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {previewData.contacts.length > 200 && (
                    <p className="text-xs text-muted-foreground px-3 py-2">
                      Showing first 200 contacts.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={isBulkImportDialogOpen} onOpenChange={setIsBulkImportDialogOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Bulk Import Contacts</DialogTitle>
            <DialogDescription>
              {bulkImportCriteria
                ? `Manage imported contacts for "${bulkImportCriteria.name}"`
                : 'Select a bulk import criteria to manage uploads.'}
            </DialogDescription>
          </DialogHeader>
          {bulkImportCriteria ? (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Upload Contacts</CardTitle>
                  <CardDescription>
                    {(() => {
                      const lastUpdated = bulkImportCriteria.lastUpdatedAt
                        ? formatDistanceToNow(new Date(bulkImportCriteria.lastUpdatedAt), { addSuffix: true })
                        : 'never';
                      const currentCount = bulkImportCriteria.matchingUserCount ?? 0;
                      return `Last updated ${lastUpdated}. Currently ${currentCount} valid contacts.`;
                    })()}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="bulk-upload-file">Contact file</Label>
                    <Input
                      id="bulk-upload-file"
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      ref={fileInputRef}
                      disabled={isUploading}
                      onChange={(event) => {
                        const file = event.target.files?.[0] || null;
                        setUploadFile(file);
                      }}
                    />
                    {uploadFile && (
                      <p className="text-xs text-muted-foreground">
                        Selected: {uploadFile.name} ({Math.round(uploadFile.size / 1024)} KB)
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={handleBulkUpload} disabled={isUploading || !uploadFile}>
                      {isUploading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isUploading && !uploadFile}
                      onClick={() => {
                        setUploadFile(null);
                        setUploadErrors([]);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                    >
                      Clear selection
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isUploading}
                      onClick={handleDownloadTemplate}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Template
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Files are limited to 5MB. Required columns: <code>email</code>, <code>first_name</code>, <code>last_name</code>. The <code>phone</code> column is optional. Use the template to ensure column headers are correct.
                  </p>
                  {uploadErrors.length > 0 && (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>{uploadErrors.length} rows contain issues</AlertTitle>
                      <AlertDescription>
                        <div className="space-y-1 max-h-32 overflow-auto">
                          {uploadErrors.slice(0, 10).map((error) => (
                            <div key={`${error.row}-${error.issues.join('-')}`} className="text-xs">
                              Row {error.row}: {error.issues.join(', ')}
                            </div>
                          ))}
                          {uploadErrors.length > 10 && (
                            <p className="text-[11px] text-muted-foreground">
                              Showing first 10 errors. Download the import to review all issues.
                            </p>
                          )}
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <CardTitle>Import History</CardTitle>
                    <CardDescription>
                      {bulkImportHistory.length > 0 && bulkImportHistory[0]?.createdAt
                        ? `Most recent import ${formatDistanceToNow(new Date(bulkImportHistory[0].createdAt), { addSuffix: true })}`
                        : 'No imports have been uploaded yet.'}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => bulkImportCriteria && refetchBulkImports()}
                      disabled={isBulkImportsFetching}
                    >
                      {isBulkImportsFetching ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCcw className="h-4 w-4 mr-2" />
                      )}
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {isBulkImportsFetching ? (
                    <div className="flex items-center justify-center py-10">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : bulkImportHistory.length > 0 ? (
                    <div className="border rounded-md">
                      <ScrollArea className="max-h-64">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Uploaded</TableHead>
                              <TableHead>File</TableHead>
                              <TableHead>Totals</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {bulkImportHistory.map((importItem: any) => (
                              <TableRow
                                key={importItem.id}
                                className={`cursor-pointer ${selectedImportId === importItem.id ? 'bg-primary/10' : ''}`}
                                onClick={() => loadImportDetails(importItem.id)}
                              >
                                <TableCell className="whitespace-nowrap text-sm">
                                  {importItem.createdAt
                                    ? format(new Date(importItem.createdAt), 'MMM d, yyyy HH:mm')
                                    : '—'}
                                  <div className="text-xs text-muted-foreground">
                                    {importItem.createdAt
                                      ? formatDistanceToNow(new Date(importItem.createdAt), { addSuffix: true })
                                      : ''}
                                  </div>
                                </TableCell>
                                <TableCell className="max-w-[200px] truncate text-sm">
                                  {importItem.originalFilename}
                                </TableCell>
                                <TableCell className="text-sm">
                                  <div className="flex flex-col gap-1">
                                    <span>Valid: {importItem.validRows}</span>
                                    <span className="text-destructive">Invalid: {importItem.invalidRows}</span>
                                    <span className="text-muted-foreground text-xs">Total: {importItem.totalRows}</span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge variant={importItem.status === 'completed' ? 'secondary' : 'outline'}>
                                    {importItem.status}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        loadImportDetails(importItem.id);
                                      }}
                                    >
                                      View
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleDownloadImport(importItem.id);
                                      }}
                                    >
                                      <Download className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-10 text-muted-foreground">
                      No imports yet. Upload a file to get started.
                    </div>
                  )}
                </CardContent>
              </Card>

              {selectedImportId && (
                <Card>
                  <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <CardTitle>Import Details</CardTitle>
                      <CardDescription>
                        {currentImportInfo
                          ? `${currentImportInfo.originalFilename} • Uploaded ${currentImportInfo.createdAt
                              ? formatDistanceToNow(new Date(currentImportInfo.createdAt), { addSuffix: true })
                              : 'recently'}`
                          : 'Select an import to see the contact list.'}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isImportDetailsLoading}
                        onClick={() => selectedImportId && loadImportDetails(selectedImportId)}
                      >
                        {isImportDetailsLoading ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <RefreshCcw className="h-4 w-4 mr-2" />
                        )}
                        Refresh
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => selectedImportId && handleDownloadImport(selectedImportId)}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download CSV
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {currentImportInfo && (
                      <>
                        <div className="flex flex-wrap gap-2 text-sm">
                          <Badge variant="outline">Valid contacts: {currentImportValidContacts}</Badge>
                          <Badge variant="outline">Invalid rows: {currentImportInvalidContacts}</Badge>
                          <Badge variant="outline">Matched users: {currentImportMatchedContacts}</Badge>
                          <Badge variant="outline">
                            Total rows: {currentImportInfo.totalRows ?? currentImportContacts.length}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-2">
                          <p className="font-medium">What makes a row invalid?</p>
                          <ul className="list-disc list-inside space-y-0.5">
                            <li>Email is missing or not in the format <code>name@domain.tld</code>.</li>
                            <li>First name or last name is missing.</li>
                            <li>The same email appears more than once in the uploaded file.</li>
                            <li>The same phone number appears more than once in the uploaded file.</li>
                            <li>
                              The email or phone number has already been imported before for this criteria
                              (duplicate across imports).
                            </li>
                            <li>The required <code>email</code> column is missing from the file.</li>
                          </ul>
                        </div>
                      </>
                    )}
                    {isImportDetailsLoading ? (
                      <div className="flex items-center justify-center py-10">
                        <Loader2 className="h-6 w-6 animate-spin" />
                      </div>
                    ) : currentImportContacts.length > 0 ? (
                      <div className="border rounded-md">
                        <ScrollArea className="max-h-72">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Email</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Phone</TableHead>
                                <TableHead>Matched User</TableHead>
                                <TableHead>Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {currentImportContacts.slice(0, 200).map((contact: any, index: number) => {
                                const errors = Array.isArray(contact.validationErrors)
                                  ? contact.validationErrors
                                  : [];
                                return (
                                  <TableRow key={`${contact.email}-${index}`}>
                                    <TableCell className="text-sm">{contact.email}</TableCell>
                                    <TableCell className="text-sm">
                                      {contact.firstName} {contact.lastName}
                                    </TableCell>
                                    <TableCell className="text-sm">{contact.phone || '—'}</TableCell>
                                    <TableCell className="text-sm">
                                      {contact.matchedUserId ? `User #${contact.matchedUserId}` : 'Unmatched'}
                                    </TableCell>
                                    <TableCell className="text-sm">
                                      {errors.length ? (
                                        <Badge variant="destructive" className="text-xs">
                                          {errors[0]}
                                        </Badge>
                                      ) : (
                                        <Badge variant="secondary" className="text-xs">Valid</Badge>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </ScrollArea>
                        {currentImportContacts.length > 200 && (
                          <p className="text-xs text-muted-foreground px-3 py-2">
                            Showing first 200 contacts.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center py-10 text-muted-foreground">
                        No contacts stored for this import.
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="py-10 text-center text-muted-foreground">
              Select a bulk import criteria to manage uploads.
            </div>
          )}
        </DialogContent>
      </Dialog>

      {selectedCriteria && (
        <UserCriteriaViewDialog
          open={isViewDialogOpen}
          onOpenChange={setIsViewDialogOpen}
          criteriaId={selectedCriteria.id}
          criteriaName={selectedCriteria.name}
          criteriaType={selectedCriteria.criteriaType}
        />
      )}
    </div>
  );
}

function CriteriaParamsForm({ form }: { form: any }) {
  const criteriaType = form.watch('criteriaType');
  useEffect(() => {
    if (criteriaType === 'bulk_import') {
      form.setValue('criteriaParams', {});
    }
  }, [criteriaType, form]);
  
  switch (criteriaType) {
    case 'new_users':
    case 'inactive_users':
      return (
        <FormField
          control={form.control}
          name="criteriaParams.days"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Number of Days</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...field}
                  onChange={(e) => field.onChange(parseInt(e.target.value))}
                  data-testid="input-criteria-days"
                />
              </FormControl>
              <FormDescription>
                {criteriaType === 'new_users' 
                  ? 'Users registered within the last X days'
                  : 'Users inactive for the last X days'}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    case 'tier_users':
      return (
        <FormField
          control={form.control}
          name="criteriaParams.tier"
          render={({ field }) => (
            <FormItem>
              <FormLabel>User Tier</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger data-testid="select-criteria-tier">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="bronze">Bronze</SelectItem>
                  <SelectItem value="silver">Silver</SelectItem>
                  <SelectItem value="gold">Gold</SelectItem>
                  <SelectItem value="platinum">Platinum</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    case 'marketing_consent':
      return (
        <FormField
          control={form.control}
          name="criteriaParams.consent"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Marketing Consent</FormLabel>
              <Select 
                onValueChange={(val) => field.onChange(val === 'true')} 
                defaultValue={field.value ? 'true' : 'false'}
              >
                <FormControl>
                  <SelectTrigger data-testid="select-criteria-consent">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="true">Has Consent</SelectItem>
                  <SelectItem value="false">No Consent</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    case 'bulk_import':
      return (
        <div className="p-4 border rounded-md bg-muted/40">
          <p className="text-sm text-muted-foreground">
            After saving this criteria, use the <strong>Bulk Import</strong> button to upload a CSV or Excel file
            containing contact details. Required columns: <code>email</code>, <code>first_name</code>,{' '}
            <code>last_name</code>. The optional <code>phone</code> column will also be stored.
          </p>
        </div>
      );
    case 'internal_testing':
      return (
        <InternalTestingUserSelector form={form} />
      );
    case 'weekly_competition_engagement':
      return (
        <FormField
          control={form.control}
          name="criteriaParams.enteredAll"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Competition Engagement</FormLabel>
              <Select
                onValueChange={(val) => field.onChange(val === 'true')}
                defaultValue={field.value ? 'true' : 'false'}
              >
                <FormControl>
                  <SelectTrigger data-testid="select-criteria-weekly-engagement">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="false">Has NOT entered all competitions this week</SelectItem>
                  <SelectItem value="true">Has entered all competitions this week</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>
                Uses the current competition week (Wednesday 10:00 UTC+2 to next Wednesday 10:00)
                and respects marketing consent.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    default:
      return null;
  }
}

function InternalTestingUserSelector({ form }: { form: any }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const latestSearchRef = useRef("");

  const selectedUserIds = form.watch('criteriaParams.userIds') || [];

  const searchUsers = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    latestSearchRef.current = query;
    setIsSearching(true);
    try {
      const response = await apiRequest('GET', `/api/admin/users/search?search=${encodeURIComponent(query)}&limit=10`);
      const data = await response.json();
      if (latestSearchRef.current !== query) {
        // Ignore stale responses
        return;
      }
      const rawResults = data.users || data || [];
      // For internal testing, show all users (no marketing consent filter)
      setSearchResults(rawResults);
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const addUser = (user: any) => {
    const currentIds = form.getValues('criteriaParams.userIds') || [];
    if (!currentIds.includes(user.id)) {
      form.setValue('criteriaParams.userIds', [...currentIds, user.id]);
    }
    setSearchQuery("");
    setSearchResults([]);
  };

  const removeUser = (userId: number) => {
    const currentIds = form.getValues('criteriaParams.userIds') || [];
    form.setValue('criteriaParams.userIds', currentIds.filter((id: number) => id !== userId));
  };

  const { data: selectedUsersData } = useQuery({
    queryKey: ['/api/admin/users/selected', selectedUserIds],
    queryFn: async () => {
      if (selectedUserIds.length === 0) return [];
      const response = await apiRequest('POST', '/api/admin/users/batch', {
        body: { userIds: selectedUserIds }
      });
      return response.json();
    },
    enabled: selectedUserIds.length > 0
  });

  return (
    <div className="space-y-4">
      <FormItem>
        <FormLabel>Select Test Users</FormLabel>
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              placeholder="Search users by email or name..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                searchUsers(e.target.value);
              }}
              data-testid="input-search-users"
            />
          </div>
          {isSearching && (
            <div className="text-sm text-muted-foreground">Searching...</div>
          )}
          {searchResults.length > 0 && (
            <Card className="p-2">
              <div className="space-y-1">
                {searchResults.map((user: any) => (
                  <Button
                    key={user.id}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start"
                onClick={() => addUser(user)}
                    disabled={selectedUserIds.includes(user.id)}
                  >
                    {user.email} - {user.firstName} {user.lastName}
                  </Button>
                ))}
              </div>
            </Card>
          )}
        </div>
        <FormDescription>
          Search and select specific users for internal testing
        </FormDescription>
      </FormItem>

      {selectedUserIds.length > 0 && (
        <Card className="p-4">
          <div className="space-y-2">
            <h4 className="font-semibold text-sm">Selected Users ({selectedUserIds.length})</h4>
            <div className="space-y-2">
              {selectedUsersData?.map((user: any) => (
                <div key={user.id} className="flex items-center justify-between p-2 bg-muted rounded-md">
                  <div className="text-sm">
                    <div className="font-medium">{user.email}</div>
                    <div className="text-muted-foreground text-xs">
                      {user.firstName} {user.lastName}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeUser(user.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
